import { useTranslation } from 'react-i18next';
import { Input, Row, Col, Select, Segmented, Switch } from 'antd';
import { BaseOptionType } from 'antd/es/select';
import { Controller, Control, FieldErrors, FieldValues } from 'react-hook-form';
import { EntitySelect, OptionAlert } from '@/components';
import {
  AlertRuleDirection,
  AlertRuleCategoryJoin,
  CompRiskLevel,
} from '@/api';
import { RiskKeys } from '@/config';
import {
  ALERT_NAME_MAX_LENGTH,
  selectPopupStyles,
  type AlertGradeOption,
  type AlertDirectionOption,
  type AlertEntityTypeOption,
} from './constants';
import cx from 'classnames';
import styles from './AlertRuleFormFields.module.sass';

const renderOptionAlert = (option: BaseOptionType) => (
  <OptionAlert value={option.value} />
);

export type AlertRuleBaseFormValues = {
  name: string;
  risk: CompRiskLevel;
  direction: AlertRuleDirection;
  categories: RiskKeys[];
  categoryJoin: AlertRuleCategoryJoin;
};

type AlertRuleFormFieldsProps = {
  control: Control<FieldValues>;
  errors: FieldErrors<FieldValues>;
  alertGradeOptions: AlertGradeOption[];
  alertDirectionOptions: AlertDirectionOption[];
  alertEntityTypeOptions: AlertEntityTypeOption[];
  isShowBlacklistedField?: boolean;
};

export const AlertRuleFormFields = ({
  control,
  errors,
  alertGradeOptions,
  alertDirectionOptions,
  alertEntityTypeOptions,
  isShowBlacklistedField = false,
}: AlertRuleFormFieldsProps) => {
  const { t } = useTranslation();

  const categoryJoinOptions = [
    { label: t('translation.category_join_all'), value: 'all' },
    { label: t('translation.category_join_any'), value: 'any' },
  ];

  return (
    <>
      <div className={styles.field}>
        <div className={styles.label}>
          {t('translation.rule_name')}
          <sup>*</sup>
        </div>
        <Controller
          name="name"
          control={control}
          rules={{ required: true, minLength: 1 }}
          render={({ field }) => (
            <Input
              {...field}
              size="large"
              type="text"
              id="name"
              maxLength={ALERT_NAME_MAX_LENGTH}
              status={errors.name ? 'error' : undefined}
              autoFocus
            />
          )}
        />
      </div>

      <Row gutter={20}>
        <Col xs={24} lg={12}>
          <div className={styles.field}>
            <div className={styles.label}>
              {t('translation.alert_grade')}
              <sup>*</sup>
            </div>
            <Controller
              name="risk"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <Select
                  {...field}
                  size="large"
                  options={alertGradeOptions}
                  styles={selectPopupStyles}
                  popupMatchSelectWidth={false}
                  labelRender={renderOptionAlert}
                  optionRender={renderOptionAlert}
                  status={errors.risk ? 'error' : undefined}
                  className={styles.selectWhite}
                />
              )}
            />
          </div>
        </Col>
        <Col xs={24} lg={12}>
          <div className={styles.field}>
            <div className={styles.label}>
              {t('translation.direction')}
              <sup>*</sup>
            </div>
            <Controller
              name="direction"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <Select
                  {...field}
                  size="large"
                  options={alertDirectionOptions}
                  styles={selectPopupStyles}
                  status={errors.direction ? 'error' : undefined}
                  className={styles.selectWhite}
                />
              )}
            />
          </div>
        </Col>
      </Row>

      <Row gutter={20}>
        <Col span={24}>
          <div className={cx(styles.field, styles.entityTypeField)}>
            <div className={styles.label}>
              {t('translation.categories')}
              <sup>*</sup>
            </div>
            <Controller
              name="categories"
              control={control}
              rules={{
                required: true,
                validate: (v): true | string =>
                  (Array.isArray(v) && v.length > 0) ||
                  (t('translation.select_at_least_one_category') ?? ''),
              }}
              render={({ field }) => (
                <EntitySelect
                  value={field.value}
                  options={alertEntityTypeOptions}
                  notFoundContent={t(
                    'translation.no_matching_categories_found',
                  )}
                  popupMatchSelectWidth={false}
                  status={errors.categories ? 'error' : undefined}
                  onChange={(v) => field.onChange(v ?? [])}
                  isMultiple
                />
              )}
            />

            <div className={styles.categoryOptionsRow}>
              <Controller
                name="categoryJoin"
                control={control}
                render={({ field }) => (
                  <Segmented
                    value={field.value ?? 'all'}
                    options={categoryJoinOptions}
                    className={styles.segmented}
                    onChange={(val) =>
                      field.onChange(val === 'any' ? 'any' : 'all')
                    }
                  />
                )}
              />

              {isShowBlacklistedField && (
                <Controller
                  name="params.isBlacklisted"
                  control={control}
                  render={({ field }) => (
                    <label className={styles.switchRow}>
                      <Switch
                        checked={!!field.value}
                        onChange={field.onChange}
                      />
                      <span className={styles.label}>
                        {t('translation.check_if_counterparty_blacklisted')}
                      </span>
                    </label>
                  )}
                />
              )}
            </div>
          </div>
        </Col>
      </Row>
    </>
  );
};
